import { Box, VStack, motion, system } from '@hover/blueprint';
import { breakpoints } from '@hover/blueprint/foundation';
import { iconInspiration } from '@hover/icons';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { EmptyIdeas } from 'src/modules/homeownerApp/components/Projects/Project/IdeasSection/EmptyIdeas';
import { IdeasImageContainer } from 'src/modules/homeownerApp/components/Projects/Project/IdeasSection/IdeasImageContainer';
import {
  HeaderSection,
  HeaderSectionButtons,
  PROJECT_DETAILS_PADDING,
} from 'src/modules/homeownerApp/components/Projects/Project/ProjectCardSection';
import { getStylesRoute } from 'src/modules/homeownerApp/components/Projects/Project/helpers';
import { useActiveOriginalImage } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';
import type {
  AiDisplayImage,
  AiOriginalImage,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { Provider as IdeasProvider } from './useIdeas';

type IdeasSectionProps = {
  projectId: string;
  image?: AiDisplayImage;
};

const MotionSkeleton = motion(Skeleton);
const MotionVStack = motion(VStack);

export const IdeasSection = ({ image, projectId }: IdeasSectionProps) => {
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [scroll, setScroll] = useState<
    (direction: 'next' | 'previous') => void
  >(() => {});
  const { image: currentImage, loading: isLoading } = useActiveOriginalImage({
    originalImageId: image?.aiOriginalImageId,
  });

  if (isLoading) {
    return (
      <MotionSkeleton
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        flex={1}
        initial={{ opacity: 0 }}
        isLoaded={!isLoading}
        mx={PROJECT_DETAILS_PADDING}
        rounded="400"
      >
        <system.div height="150px" minWidth={1} />
      </MotionSkeleton>
    );
  }

  if (!currentImage) return <EmptyIdeas />;

  const images = (currentImage as AiOriginalImage).aiDisplayImages ?? [];

  return (
    <IdeasProvider
      value={{
        canScrollLeft,
        canScrollRight,
        scroll,
        setCanScrollLeft,
        setCanScrollRight,
        setScroll,
      }}
    >
      <MotionVStack
        animate={{ opacity: 1 }}
        background="neutral.50"
        data-testid="ideas-section"
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        overflow="hidden"
        py={{ base: 800, tablet: 950 }}
        width="100%"
      >
        <VStack alignItems="center" maxW={breakpoints.width500} mx="auto">
          <Box alignItems="center" justifyContent="space-between" w="100%">
            <HeaderSection
              subtitle={t('projects.ideas.subtitle')}
              title={t('projects.ideas.title')}
            />
            <HeaderSectionButtons />
          </Box>
          <IdeasImageContainer
            data-testid="ideas-image-container"
            images={images}
            originalImage={currentImage}
            projectId={projectId}
            ref={imageContainerRef}
          />
          <Box data-testid="ideas-more-styles" paddingTop={600}>
            <Button
              as={Link}
              iconAfter={iconInspiration}
              size="large"
              to={getStylesRoute(image, projectId)}
            >
              {t('projects.ideas.viewMoreStyles')}
            </Button>
          </Box>
        </VStack>
      </MotionVStack>
    </IdeasProvider>
  );
};
